import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Upload, X, Package, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, pimg, errMsg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", slug: "", description: "", image: "" };
const emptyProduct = { name: "", price: "", description: "", image: "", available: true, stock: 10 };

const AdminCategories = () => {
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [itemsFor, setItemsFor] = useState(null);
  const [pOpen, setPOpen] = useState(false);
  const [pEditing, setPEditing] = useState(null);
  const [pForm, setPForm] = useState(emptyProduct);
  const [pSaving, setPSaving] = useState(false);
  const [pUploading, setPUploading] = useState(false);
  const [pCat, setPCat] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/categories"), api.get("/products")]).then(([c, p]) => {
      setCats(c.data); setProducts(p.data); setLoading(false);
    });
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm(c); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, form);
      else await api.post("/admin/categories", form);
      toast.success(editing ? "Category updated" : "Category added");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); load();
  };

  const catProducts = (slug) => products.filter((p) => p.category_slug === slug);

  const openProductNew = (cat) => { setPEditing(null); setPCat(cat); setPForm({ ...emptyProduct }); setPOpen(true); };
  const openProductEdit = (p) => {
    setPEditing(p);
    setPForm({ name: p.name, price: String(p.price), description: p.description || "", image: p.image || "", available: !!p.available, stock: p.stock ?? 0 });
    setPOpen(true);
  };

  const pUpload = async (file) => {
    setPUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPUploading(false); }
  };

  const saveProduct = async () => {
    if (!pForm.name || !pForm.price) { toast.error("Name and price are required"); return; }
    setPSaving(true);
    try {
      const base = pEditing
        ? { name: pEditing.name, category_slug: pEditing.category_slug, price: pEditing.price, weight: pEditing.weight, description: pEditing.description, ingredients: pEditing.ingredients, storage_instructions: pEditing.storage_instructions, image: pEditing.image, available: pEditing.available, best_seller: pEditing.best_seller, stock: pEditing.stock, weight_grams: pEditing.weight_grams || 0, customizable: !!pEditing.customizable, eggless_available: !!pEditing.eggless_available }
        : { name: "", category_slug: pCat.slug, price: 0, weight: "", description: "", ingredients: "", storage_instructions: "", image: "", available: true, best_seller: false, stock: 0, weight_grams: 0, customizable: false, eggless_available: false };
      const payload = { ...base, name: pForm.name, price: parseFloat(pForm.price), description: pForm.description, image: pForm.image, available: pForm.available, stock: parseInt(pForm.stock) || 0 };
      if (pEditing) await api.put(`/admin/products/${pEditing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(pEditing ? "Product updated" : "Product added");
      setPOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPSaving(false); }
  };

  const delProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Product deleted"); load();
  };

  return (
    <AdminLayout title="Categories">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">{cats.length} categories</p>
        <button data-testid="add-category-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-xl bg-choco text-cream font-semibold px-5 py-3 hover:bg-chocodark transition-colors min-h-[48px]"><Plus size={18} /> Add Category</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-goldsoft" /></div>
      ) : (
        <div className="space-y-4">
          {cats.map((c) => {
            const list = catProducts(c.slug);
            const expanded = itemsFor?.slug === c.slug;
            return (
              <div key={c.id} data-testid={`admin-category-${c.slug}`} className="bg-white rounded-2xl border border-[#E5DFD3] overflow-hidden">
                <div className="flex flex-col sm:flex-row">
                  <img src={pimg(c.image)} alt={c.name} className="h-28 w-full sm:h-auto sm:w-40 object-cover shrink-0" />
                  <div className="p-4 flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-display text-lg font-semibold text-choco">{c.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{c.description}</p>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-cream text-choco shrink-0">{list.length} items</span>
                    </div>
                    <div className="flex gap-4 mt-3 flex-wrap">
                      <button data-testid={`view-items-${c.slug}`} onClick={() => setItemsFor(expanded ? null : c)} className="inline-flex items-center gap-1 text-sm font-medium text-goldsoft hover:text-choco">
                        <Package size={15} /> {expanded ? "Hide Items" : "View Items"} <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </button>
                      <button onClick={() => openEdit(c)} className="inline-flex items-center gap-1 text-sm text-choco hover:text-goldsoft"><Pencil size={15} /> Edit</button>
                      <button onClick={() => del(c)} className="inline-flex items-center gap-1 text-sm text-destructive hover:opacity-70"><Trash2 size={15} /> Delete</button>
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div data-testid={`category-items-${c.slug}`} className="border-t border-[#E5DFD3] bg-cream/50 p-4">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-sm font-medium text-choco">Products in {c.name}</p>
                      <button data-testid={`add-item-${c.slug}`} onClick={() => openProductNew(c)} className="inline-flex items-center gap-2 rounded-xl bg-choco text-cream text-sm font-semibold px-4 py-2.5 hover:bg-chocodark transition-colors min-h-[44px]"><Plus size={16} /> Add Item</button>
                    </div>
                    {list.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No products in this category yet. Click "Add Item" to create one.</p>
                    ) : (
                      <div className="space-y-2">
                        {list.map((p) => (
                          <div key={p.id} data-testid={`category-product-${p.id}`} className="bg-white rounded-xl border border-[#E5DFD3] p-3 flex items-center gap-3">
                            <img src={pimg(p.image)} alt={p.name} className="h-14 w-14 rounded-lg object-cover shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-choco text-sm line-clamp-1">{p.name}</p>
                              <p className="font-display font-bold text-choco text-sm">{formatINR(p.price)}</p>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.available ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{p.available ? "Available" : "Hidden"}</span>
                                {(p.stock ?? 0) <= 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800">Out of stock</span>}
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <button data-testid={`category-edit-product-${p.id}`} onClick={() => openProductEdit(p)} className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-[#E5DFD3] text-choco hover:border-goldsoft"><Pencil size={15} /></button>
                              <button data-testid={`category-delete-product-${p.id}`} onClick={() => delProduct(p)} className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-[#E5DFD3] text-destructive hover:opacity-70"><Trash2 size={15} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream">
          <DialogHeader><DialogTitle className="font-display text-2xl text-choco">{editing ? "Edit Category" : "Add Category"}</DialogTitle><DialogDescription className="sr-only">Category details form</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-choco">Name</label><Input data-testid="category-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" /></div>
            <div><label className="text-sm font-medium text-choco">Slug (optional)</label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" className="mt-1 h-11 bg-white border-[#E5DFD3]" /></div>
            <div><label className="text-sm font-medium text-choco">Description</label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white border-[#E5DFD3]" /></div>
            <div><label className="text-sm font-medium text-choco">Image URL</label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" /></div>
            <button data-testid="category-form-save" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark transition-colors disabled:opacity-60">{saving && <Loader2 size={18} className="animate-spin" />} Save</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-choco">{pEditing ? "Edit Item" : `Add Item to ${pCat?.name || ""}`}</DialogTitle><DialogDescription className="sr-only">Product details form</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-choco">Name</label>
              <Input data-testid="category-product-form-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-choco">Price (₹)</label>
                <Input data-testid="category-product-form-price" type="number" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
              <div>
                <label className="text-sm font-medium text-choco">Stock</label>
                <Input data-testid="category-product-form-stock" type="number" value={pForm.stock} onChange={(e) => setPForm({ ...pForm, stock: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Description</label>
              <Textarea data-testid="category-product-form-description" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} className="mt-1 bg-white border-[#E5DFD3]" />
            </div>
            <div>
              <label className="text-sm font-medium text-choco">Image</label>
              <div className="flex gap-3 items-center mt-1">
                {pForm.image ? (
                  <div className="relative">
                    <img src={pimg(pForm.image)} alt="" className="h-16 w-16 rounded-xl object-cover" />
                    <button onClick={() => setPForm({ ...pForm, image: "" })} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full h-5 w-5 flex items-center justify-center"><X size={12} /></button>
                  </div>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-xl border border-[#E5DFD3] bg-white px-4 py-2.5 text-sm cursor-pointer hover:border-goldsoft">
                  {pUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
                  <input data-testid="category-product-form-upload" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && pUpload(e.target.files[0])} />
                </label>
              </div>
              <Input value={pForm.image} onChange={(e) => setPForm({ ...pForm, image: e.target.value })} placeholder="or paste image URL" className="mt-2 h-10 bg-white border-[#E5DFD3] text-xs" />
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl border border-[#E5DFD3] px-4 py-3">
              <span className="text-sm font-medium text-choco">Available (visible on shop)</span>
              <Switch data-testid="category-product-form-available" checked={pForm.available} onCheckedChange={(v) => setPForm({ ...pForm, available: v })} />
            </div>
            <button data-testid="category-product-form-save" onClick={saveProduct} disabled={pSaving} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark transition-colors disabled:opacity-60">
              {pSaving && <Loader2 size={18} className="animate-spin" />} {pEditing ? "Update Item" : "Add Item"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCategories;
